import React, { useEffect, useState } from 'react'

import {
  StatusBannerBody,
  StatusBannerData,
  StatusBannerLevel
} from 'utils/types'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import MenuItem from 'components_new/atoms/Menu/MenuItem'

import Grid from 'components_new/atoms/Grid'

import SelectSearch from 'components_new/molecules/SelectSearch'
import TextField from 'components_new/atoms/TextField'

import StatusBanner from 'components_new/molecules/StatusBanner'
import { appendEmailToLink } from 'components_new/organisms/BannerBar'
import InputAdornment from 'components_new/atoms/InputAdornment'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SwitchItem from 'components_new/molecules/SwitchItem'
import { useTranslation } from 'language/translator'

import { InternalCustomerOption } from 'types/GlobalCustomer'

interface StatusBannerTemplateProps {
  submit: (data: StatusBannerBody) => void
  customerOptions: InternalCustomerOption[]
  data?: StatusBannerData
  onClose: () => void
  open: boolean
}

const INIT_STATE: StatusBannerBody = {
  customer_ids: [],
  description: '',
  link: null,
  link_label: null,
  append_email_to_link: false,
  level: 'info',
  visible: true,
  title: '',
  only_admins: false
}

const PREVIEW_EMAIL = 'namn@exempel.se'

const StatusBannerTemplate = (props: StatusBannerTemplateProps) => {
  const { customerOptions, data, onClose, open, submit } = props
  const [formData, setFormData] = useState<StatusBannerBody>(INIT_STATE)
  const isEdit = !!data
  const { t } = useTranslation()

  useEffect(() => {
    if (data) {
      setFormData(data)
    } else {
      setFormData(INIT_STATE)
    }
  }, [data])

  return (
    <SimpleDialog
      actions={
        <>
          <Button variant="text" onClick={() => onClose()}>
            {t('global', 'cancel')}
          </Button>
          <Button onClick={() => submit(formData)}>
            {isEdit ? t('global', 'save') : t('global', 'publish')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={isEdit ? 'Uppdatera banner' : 'Skapa banner'}
      maxWidth="lg"
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionHeader title="Innehåll" />
            <Box sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Nivå"
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      level: event.target.value as StatusBannerLevel
                    })
                  }
                  select={true}
                  size="small"
                  sx={{ width: 120 }}
                  value={formData.level}
                >
                  <MenuItem value="info">
                    {t('status_banner', 'level_info')}
                  </MenuItem>
                  <MenuItem value="important">
                    {t('status_banner', 'level_important')}
                  </MenuItem>
                </TextField>

                <TextField
                  label="Titel"
                  onChange={(event) =>
                    setFormData({ ...formData, title: event.target.value })
                  }
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.title.length}/25
                      </InputAdornment>
                    ),
                    inputProps: {
                      maxLength: 25
                    }
                  }}
                  value={formData.title}
                  required
                />
              </Box>

              <TextField
                label="Beskrivning"
                multiline={true}
                minRows={2}
                maxRows={3}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    description: event.target.value
                  })
                }
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {formData.description.length}/100
                    </InputAdornment>
                  ),
                  inputProps: {
                    maxLength: 100
                  }
                }}
                value={formData.description}
                required
              />
            </Box>

            <SectionHeader title="Länk" />
            <Box sx={{ pb: 2 }}>
              <TextField
                label="URL"
                onChange={(event) =>
                  setFormData({ ...formData, link: event.target.value })
                }
                size="small"
                value={formData.link ?? ''}
              />
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {formData.link_label?.length ?? 0}/25
                    </InputAdornment>
                  ),
                  inputProps: {
                    maxLength: 25
                  }
                }}
                label="Text"
                onChange={(event) =>
                  setFormData({ ...formData, link_label: event.target.value })
                }
                size="small"
                value={formData.link_label ?? ''}
              />
              <SwitchItem
                checked={formData.append_email_to_link ?? false}
                label={'Lägg till användarens e-post i länken (?email=)'}
                onChange={() =>
                  setFormData({
                    ...formData,
                    append_email_to_link: !formData.append_email_to_link
                  })
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionHeader title="Kunder" />
            <Box sx={{ pb: 2 }}>
              <SelectSearch
                helperText={
                  <>
                    Om ingen kund är vald kommer <b>alla</b> att se bannern.
                  </>
                }
                onChange={(options) =>
                  setFormData({
                    ...formData,
                    customer_ids: options.map((option) => option.id)
                  })
                }
                multiple
                selected={customerOptions.filter((option) =>
                  formData.customer_ids.includes(option.id)
                )}
                options={customerOptions}
              />
            </Box>

            <SectionHeader title="Synlighet" />
            <Box sx={{ pb: 2 }}>
              <Box>
                <SwitchItem
                  checked={formData.visible}
                  label={'Visa i portal'}
                  onChange={() =>
                    setFormData({ ...formData, visible: !formData.visible })
                  }
                />
              </Box>
              <Box>
                <SwitchItem
                  checked={formData.only_admins}
                  label={'Endast administratörer'}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      only_admins: !formData.only_admins
                    })
                  }
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SectionHeader title="Förhandsvisning" />
      {formData.visible && (
        <StatusBanner
          banners={[
            {
              id: 'preview',
              level: formData.level,
              title: formData.title,
              description: formData.description,
              href: appendEmailToLink(
                formData.link,
                formData.append_email_to_link,
                PREVIEW_EMAIL
              ),
              linkLabel: formData.link_label
            }
          ]}
          expanded={false}
          onToggleExpanded={() => null}
        />
      )}
    </SimpleDialog>
  )
}

export default StatusBannerTemplate
